// Lo que la página de una agencia (pages/AgenciaDetail.jsx) escribe en
// Firestore con la sesión del admin. Las reglas dejan al ADMIN cambiar
// cualquier campo de agencies/{id} (firestore.rules › isAdmin).
//
// La lista (pages/Agencias.jsx) tiene su propio validar y su bienvenida: los
// tests de la app buscan ese código allí, tal cual.

import {
  deleteField,
  doc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { apiTry } from './api';
import { deleteAgencyCascade, purgeSummary } from './deleteAgency';

/** Escribe { set, remove } (lo que devuelven planPatch / profilePatch). */
export async function saveAgencyPatch(agencyId, { set = {}, remove = [] } = {}) {
  const patch = { ...set, updatedAt: serverTimestamp() };
  for (const key of remove) {
    if (!(key in set)) patch[key] = deleteField();
  }
  await updateDoc(doc(db, 'agencies', agencyId), patch);
}

// Bienvenida al Owner («Tu agencia ya está activa en TikControl»): el Worker
// decide si toca (validada, con Owner, una vez por Owner). Devuelve el texto
// que se añade al aviso, o ''.
export async function sendOwnerWelcome(agencyId, ownerUid) {
  try {
    const body = ownerUid ? { agencyId, ownerUid } : { agencyId };
    const res = await apiTry('/api/agency/owner-welcome', { method: 'POST', body });
    return res.ok && res.data?.sent > 0 ? ' Correo de bienvenida enviado al Owner.' : '';
  } catch (_) {
    return '';
  }
}

export async function setAgencyValidated(agency, next) {
  await updateDoc(doc(db, 'agencies', agency.id), {
    validated: next,
    validatedAt: next ? serverTimestamp() : null,
    validatedBy: next ? auth.currentUser?.uid || null : null,
    updatedAt: serverTimestamp()
  });
  return next ? sendOwnerWelcome(agency.id) : '';
}

// Borrado en cascada, con las mismas dos preguntas que la lista. Devuelve
// { ok, msg } o null si se ha cancelado.
export async function confirmAndDeleteAgency(agency) {
  const warn = [
    `¿Borrar "${agency.name || agency.id}"?`,
    '',
    'Se borrarán también sus creadores (con el histórico importado del backstage), '
      + 'misiones, eventos, perfiles, licencias, biblioteca, whitelist y los roles de '
      + 'acceso de sus owners y managers.',
    '',
    'A sus creadores con licencia se les retira (se reanuda el cobro que pausó la '
      + 'agencia y vuelven a su plan), y se borran su Backstage, archivos, tracking y avisos.',
    '',
    'Esta acción no se puede deshacer.'
  ].join('\n');
  if (!confirm(warn)) return null;
  const typed = prompt(`Escribe ${agency.id} para confirmar el borrado:`);
  if ((typed || '').trim().toUpperCase() !== String(agency.id).toUpperCase()) {
    return { ok: false, msg: 'ID incorrecto: no se ha borrado nada.' };
  }
  const res = await deleteAgencyCascade(db, agency.id);
  const docs = Object.values(res.deleted || {}).reduce((acc, n) => acc + (n || 0), 0);
  const extra = res.purge?.report ? ` · Limpieza: ${purgeSummary(res.purge.report)}` : '';
  if (res.errors?.length) {
    return { ok: false, deleted: true, msg: `Agencia eliminada, pero algo falló: ${res.errors[0]} (en «Restos de borradas» se puede repetir la limpieza)${extra}` };
  }
  return { ok: true, deleted: true, msg: `Agencia eliminada · ${docs} documentos borrados${extra}.` };
}
